// ─────────────────────────────────────────────
// PoliciesSecurityPage.jsx
// ─────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
import { Shield, Plus, Edit2, Trash2, X, Eye, Search } from 'lucide-react';

const EMPTY_POLICY = { name: '', description: '', platform: 'All', rules: '', isActive: true, priority: 'Medium' };

export const DevicePoliciesPage = () => {
  const [devices, setDevices] = useState([]);
  const [policies, setPolicies] = useState([
    { id: 1, name: 'Corporate Security Policy', description: 'Standard security for all company devices', platform: 'All', rules: 'PIN required, Encryption enabled, No sideloading', isActive: true, priority: 'High', deviceCount: 0 },
    { id: 2, name: 'BYOD Policy', description: 'Policy for personal devices accessing company resources', platform: 'Android', rules: 'Work profile required, VPN mandatory', isActive: true, priority: 'Medium', deviceCount: 0 },
    { id: 3, name: 'iOS MDM Profile', description: 'Default iOS management profile', platform: 'iOS', rules: 'App store restrictions, Screen time limits', isActive: true, priority: 'High', deviceCount: 0 },
  ]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(EMPTY_POLICY);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    deviceController.getAll().then(r => {
      if (r.success) {
        setDevices(r.data);
        setPolicies(prev => prev.map(p => ({ ...p, deviceCount: r.data.filter(d => d.policyName === p.name).length })));
      }
      setLoading(false);
    });
  }, []);

  const handleInput = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#ef444420', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    btnPrimary: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    btnSecond: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    btnDanger: { backgroundColor: '#ef4444', color: '#fff', border: 'none' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '22px' },
    statCard: { backgroundColor: '#fff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: '3px solid #ef4444' },
    statLabel: { fontSize: '12px', color: '#64748b', marginBottom: '6px' },
    statVal: { fontSize: '26px', fontWeight: '700' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '13px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '13px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
    actBtn: { padding: '5px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    mContent: { backgroundColor: '#fff', borderRadius: '14px', width: '90%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto' },
    mHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 },
    mTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: 0 },
    mBody: { padding: '24px' },
    mFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    fGroup: { marginBottom: '16px' },
    label: { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '80px', boxSizing: 'border-box' },
  };

  const priorityColor = { High: '#ef4444', Medium: '#f59e0b', Low: '#10b981' };
  const platformColor = { All: '#8b5cf6', Android: '#34a853', iOS: '#007aff', Windows: '#0078d4' };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><Shield size={22} color="#ef4444" /></div>
          <div><h1 style={s.title}>Policies & Security</h1><div style={s.subtitle}>{policies.length} policies configured</div></div>
        </div>
        <button style={s.btnPrimary} onClick={() => { setFormData(EMPTY_POLICY); setShowAdd(true); }}><Plus size={15} /> New Policy</button>
      </div>

      <div style={s.statsGrid}>
        {[['Total Policies', policies.length, '#ef4444'], ['Active', policies.filter(p => p.isActive).length, '#10b981'], ['High Priority', policies.filter(p => p.priority === 'High').length, '#ef4444'], ['Non-Compliant Devices', devices.filter(d => d.complianceStatus === 'Non-Compliant').length, '#f59e0b']].map(([l, v, c]) => (
          <div key={l} style={{ ...s.statCard, borderLeftColor: c }}><div style={s.statLabel}>{l}</div><div style={{ ...s.statVal, color: c }}>{v}</div></div>
        ))}
      </div>

      <div style={s.card}>
        {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading policies...</div> : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Policy Name</th>
                <th style={s.th}>Platform</th>
                <th style={s.th}>Priority</th>
                <th style={s.th}>Devices</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {policies.map(policy => (
                <tr key={policy.id}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={s.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '8px', backgroundColor: '#ef444415', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Shield size={15} color="#ef4444" /></div>
                      <div>
                        <div style={{ fontWeight: '500' }}>{policy.name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{policy.description}</div>
                      </div>
                    </div>
                  </td>
                  <td style={s.td}><span style={{ ...s.badge, backgroundColor: `${platformColor[policy.platform] || '#6b7280'}15`, color: platformColor[policy.platform] || '#6b7280' }}>{policy.platform}</span></td>
                  <td style={s.td}><span style={{ ...s.badge, backgroundColor: `${priorityColor[policy.priority]}15`, color: priorityColor[policy.priority] }}>{policy.priority}</span></td>
                  <td style={s.td}><span style={{ fontWeight: '600' }}>{policy.deviceCount}</span> devices</td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, backgroundColor: policy.isActive ? '#10b98115' : '#6b728015', color: policy.isActive ? '#10b981' : '#6b7280' }}>
                      {policy.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <button style={s.actBtn} onClick={() => { setSelected(policy); setFormData({ name: policy.name, description: policy.description, platform: policy.platform, rules: policy.rules, isActive: policy.isActive, priority: policy.priority }); setShowEdit(true); }}><Edit2 size={14} color="#3b82f6" /></button>
                      <button style={s.actBtn} onClick={() => { setSelected(policy); setShowDelete(true); }}><Trash2 size={14} color="#ef4444" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAdd || showEdit) && (
        <div style={s.modal} onClick={() => { setShowAdd(false); setShowEdit(false); }}>
          <div style={s.mContent} onClick={e => e.stopPropagation()}>
            <div style={s.mHeader}><h2 style={s.mTitle}>{showAdd ? 'Create Policy' : 'Edit Policy'}</h2><button style={s.actBtn} onClick={() => { setShowAdd(false); setShowEdit(false); }}><X size={18} /></button></div>
            <form onSubmit={async (e) => {
              e.preventDefault(); setSubmitting(true);
              await new Promise(r => setTimeout(r, 400));
              if (showAdd) setPolicies(prev => [...prev, { id: Date.now(), ...formData, deviceCount: 0 }]);
              else setPolicies(prev => prev.map(p => p.id === selected.id ? { ...p, ...formData } : p));
              setShowAdd(false); setShowEdit(false); setSubmitting(false);
            }}>
              <div style={s.mBody}>
                <div style={s.fGroup}><label style={s.label}>Policy Name *</label><input name="name" style={s.input} value={formData.name} onChange={handleInput} required /></div>
                <div style={s.fGroup}><label style={s.label}>Description</label><textarea name="description" style={s.textarea} value={formData.description} onChange={handleInput} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div style={s.fGroup}><label style={s.label}>Platform</label>
                    <select name="platform" style={s.input} value={formData.platform} onChange={handleInput}>
                      <option value="All">All</option><option value="Android">Android</option><option value="iOS">iOS</option><option value="Windows">Windows</option>
                    </select>
                  </div>
                  <div style={s.fGroup}><label style={s.label}>Priority</label>
                    <select name="priority" style={s.input} value={formData.priority} onChange={handleInput}>
                      <option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option>
                    </select>
                  </div>
                </div>
                <div style={s.fGroup}><label style={s.label}>Rules / Restrictions</label><textarea name="rules" style={s.textarea} value={formData.rules} onChange={handleInput} placeholder="e.g. PIN required, Encryption enabled..." /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="checkbox" name="isActive" id="isActive" checked={formData.isActive} onChange={handleInput} style={{ width: '16px', height: '16px', accentColor: '#10b981', cursor: 'pointer' }} />
                  <label htmlFor="isActive" style={{ ...s.label, margin: 0, cursor: 'pointer' }}>Policy is Active</label>
                </div>
              </div>
              <div style={s.mFooter}><button type="button" style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => { setShowAdd(false); setShowEdit(false); }}>Cancel</button><button type="submit" style={s.btnPrimary} disabled={submitting}>{submitting ? 'Saving...' : (showAdd ? 'Create' : 'Save')}</button></div>
            </form>
          </div>
        </div>
      )}

      {showDelete && selected && (
        <div style={s.modal} onClick={() => setShowDelete(false)}>
          <div style={{ ...s.mContent, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
            <div style={s.mHeader}><h2 style={s.mTitle}>Delete Policy</h2><button style={s.actBtn} onClick={() => setShowDelete(false)}><X size={18} /></button></div>
            <div style={s.mBody}><div style={{ textAlign: 'center' }}><div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Trash2 size={24} color="#ef4444" /></div><p style={{ color: '#1e293b', fontWeight: '500' }}>Delete "{selected.name}"?</p><p style={{ fontSize: '13px', color: '#64748b' }}>This policy will be removed from all devices.</p></div></div>
            <div style={s.mFooter}><button style={{ ...s.btnPrimary, ...s.btnSecond }} onClick={() => setShowDelete(false)}>Cancel</button><button style={{ ...s.btnPrimary, ...s.btnDanger }} onClick={() => { setPolicies(prev => prev.filter(p => p.id !== selected.id)); setShowDelete(false); }}>Delete</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicePoliciesPage;


